const REQUEST_TIMEOUT_MS = 10000;
const RESULT_LIMIT = 25;
// The Met's search only returns IDs, so each result costs an extra request.
// Many matches have no released image, so look through a few extra candidates,
// in small batches to stay well under the Met's rate limit.
const MET_RESULT_LIMIT = 12;
const MET_CANDIDATE_LIMIT = 36;
const MET_BATCH_SIZE = 6;

const fetchJson = async (url, signal) => {
  // 🛡️ SECURITY: Add timeout to prevent application hang if API is unresponsive (DoS mitigation)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort);
  }

  let res;
  try {
    res = await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener("abort", onAbort);
  }

  if (!res.ok) {
    throw new Error(`error loading ${url} (${res.status})`);
  }
  return res.json();
};

// Every museum adapter resolves to a list of artworks shaped like:
// { id, title, artist, imageUrl, alt, placeholder: { lqip, width, height } | null }

// https://api.artic.edu/docs/
// http://api.artic.edu/docs/#images
export const artworkImageUrl = (id, width = 843) =>
  `https://www.artic.edu/iiif/2/${id}/full/${width},/0/default.jpg`;

const searchArtic = async (query, signal) => {
  const params = new URLSearchParams({
    q: query,
    limit: String(RESULT_LIMIT),
    fields: "id,title,artist_title,image_id,thumbnail",
  });
  const { data = [] } = await fetchJson(
    `https://api.artic.edu/api/v1/artworks/search?${params}`,
    signal
  );
  return data.map((item) => ({
    id: `artic-${item.id}`,
    title: item.title,
    artist: item.artist_title,
    imageUrl: item.image_id ? artworkImageUrl(item.image_id, 250) : null,
    alt: item.thumbnail?.alt_text || item.title,
    placeholder: item.thumbnail?.lqip ? item.thumbnail : null,
  }));
};

// https://openaccess-api.clevelandart.org/
const searchCleveland = async (query, signal) => {
  const params = new URLSearchParams({
    q: query,
    has_image: "1",
    limit: String(RESULT_LIMIT),
    fields: "id,title,creators,images",
  });
  const { data = [] } = await fetchJson(
    `https://openaccess-api.clevelandart.org/api/artworks/?${params}`,
    signal
  );
  return data.map((item) => ({
    id: `cleveland-${item.id}`,
    title: item.title,
    artist: item.creators?.[0]?.description,
    imageUrl: item.images?.web?.url || null,
    alt: item.title,
    placeholder: null,
  }));
};

// https://metmuseum.github.io/
const MET_API = "https://collectionapi.metmuseum.org/public/collection";

const toMetArtwork = (item) => ({
  id: `met-${item.objectID}`,
  title: item.title,
  artist: item.artistDisplayName,
  imageUrl: item.primaryImageSmall || null,
  alt: item.title,
  placeholder: null,
});

const searchMet = async (query, signal) => {
  const params = new URLSearchParams({
    q: query,
    hasImages: "true",
    limit: String(MET_CANDIDATE_LIMIT),
  });
  const { objectIDs } = await fetchJson(`${MET_API}/v1.1/search?${params}`, signal);
  if (!objectIDs?.length) {
    return [];
  }

  const withImages = [];
  const withoutImages = [];
  const candidates = objectIDs.slice(0, MET_CANDIDATE_LIMIT);
  for (let i = 0; i < candidates.length && withImages.length < MET_RESULT_LIMIT; i += MET_BATCH_SIZE) {
    // Skip individual objects that fail to load rather than failing the search
    const batch = await Promise.allSettled(
      candidates
        .slice(i, i + MET_BATCH_SIZE)
        .map((id) => fetchJson(`${MET_API}/v1/objects/${id}`, signal))
    );
    if (signal?.aborted) {
      throw new Error("aborted");
    }
    batch
      .filter((result) => result.status === "fulfilled")
      .forEach(({ value }) =>
        (value.primaryImageSmall ? withImages : withoutImages).push(toMetArtwork(value))
      );
  }

  // Prefer artworks with images, but still show matches if none have one
  const artworks = withImages.length ? withImages : withoutImages;
  return artworks.slice(0, MET_RESULT_LIMIT);
};

export const MUSEUMS = [
  { id: "artic", name: "Art Institute of Chicago", search: searchArtic },
  { id: "cleveland", name: "Cleveland Museum of Art", search: searchCleveland },
  { id: "met", name: "The Metropolitan Museum of Art", search: searchMet },
];

export const DEFAULT_MUSEUM = "cleveland";

export const search = (museumId, query, { signal } = {}) => {
  const museum = MUSEUMS.find((m) => m.id === museumId);
  if (!museum) {
    return Promise.reject(new Error(`unknown museum ${museumId}`));
  }
  return museum.search(query, signal);
};
